import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, MessageCircle, X } from "lucide-react";
import { useHideNearFooter } from "../../utils/useHideNearFooter";
import { useIsScrolling } from "../../utils/useIsScrolling";

const WHATSAPP_NUMBER = "919789389489";
const waLink = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

// Rule-based FAQ tree — no external service, no cost. Each node is a bot
// message plus the quick-reply options shown underneath it.
const TREE = {
  root: {
    text: "Hi there! 👋 I'm the Aavryaa Assistant. How can I help you today?",
    options: [
      { label: "📦 Track my order", next: "track" },
      { label: "🚚 Shipping & delivery", next: "shipping" },
      { label: "↩️ Returns & refunds", next: "refund" },
      { label: "📏 Size help", next: "size" },
      { label: "🧵 Bulk orders", next: "bulk" },
      { label: "🙋 Talk to a human", next: "human" },
    ],
  },
  track: {
    text: "Sure! Head to our order tracking page and enter your Order ID to see live status.",
    options: [
      { label: "Go to Track Order", link: "/track" },
      { label: "⬅ Back to menu", next: "root" },
    ],
  },
  shipping: {
    text: "We usually dispatch orders within 2-3 business days, and delivery takes 4-7 days depending on your location.",
    options: [
      { label: "Read full shipping policy", link: "/shipping-policy" },
      { label: "⬅ Back to menu", next: "root" },
    ],
  },
  refund: {
    text: "Changed your mind, or got the wrong item? Here's how our returns & refunds work.",
    options: [
      { label: "Read refund policy", link: "/refund-policy" },
      { label: "⬅ Back to menu", next: "root" },
    ],
  },
  size: {
    text: "Every product page has a size estimator to help you pick the right fit. For anything specific, our team can guide you personally.",
    options: [
      {
        label: "Chat with us on WhatsApp",
        external: waLink("Hi! I need help choosing the right size."),
      },
      { label: "⬅ Back to menu", next: "root" },
    ],
  },
  bulk: {
    text: "Looking to order in bulk for events, retail, or gifting? We offer special pricing for bulk orders.",
    options: [
      {
        label: "Enquire on WhatsApp",
        external: waLink("Hi! I'm interested in placing a bulk order."),
      },
      { label: "⬅ Back to menu", next: "root" },
    ],
  },
  human: {
    text: "No problem, our support team is happy to help directly.",
    options: [
      { label: "Chat on WhatsApp", external: waLink("Hi! I need some help.") },
      { label: "Call us", external: "tel:9789389489" },
      { label: "⬅ Back to menu", next: "root" },
    ],
  },
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [nodeId, setNodeId] = useState("root");
  const [messages, setMessages] = useState([
    { sender: "bot", text: TREE.root.text },
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckoutFlow = location.pathname === "/cart" || location.pathname === "/checkout";
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  // Lock page scroll while the chat is open so wheel/touch gestures over
  // the panel always act on the message list instead of leaking to the page.
  useEffect(() => {
    if (!isOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  const handleOption = (option) => {
    setMessages((prev) => [...prev, { sender: "user", text: option.label }]);

    if (option.link) {
      setIsOpen(false);
      navigate(option.link);
      return;
    }

    if (option.external) {
      window.open(option.external, "_blank", "noopener,noreferrer");
      return;
    }

    const nextNode = TREE[option.next];
    setNodeId(option.next);
    setMessages((prev) => [...prev, { sender: "bot", text: nextNode.text }]);
  };

  const currentOptions = TREE[nodeId].options;
  const nearFooter = useHideNearFooter();
  const isScrolling = useIsScrolling();
  const fadeBubble = isScrolling && !isOpen;

  if ((nearFooter || isCheckoutFlow) && !isOpen) return null;

  return (
    <div className="fixed bottom-5 max-md:bottom-[76px] left-5 z-50 flex flex-col items-start gap-3">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="flex w-[92vw] max-w-[360px] flex-col overflow-hidden rounded-3xl border border-[var(--brand-primary-100)] bg-white shadow-2xl ring-1 ring-black/5"
            style={{ height: "min(65vh, 460px)" }}
          >
            <div className="flex items-center justify-between bg-gradient-to-r from-[var(--brand-primary-800)] via-[var(--brand-primary-700)] to-[var(--brand-secondary-600)] px-4 py-3.5 text-white">
              <div className="flex items-center gap-2.5">
                <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                  <Bot size={19} />
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[var(--brand-primary-700)] bg-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight">
                    Aavryaa Assistant
                  </p>
                  <p className="text-[11px] leading-tight text-[var(--brand-primary-200)]">
                    Usually replies instantly
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
                className="rounded-full p-1.5 text-[var(--brand-primary-100)] transition hover:bg-white/15 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div
              ref={scrollRef}
              className="chatbot-scroll flex-1 space-y-3 overflow-y-auto overscroll-contain bg-[var(--brand-primary-50)]/50 px-3 py-4"
            >
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={`flex items-end gap-2 ${
                    msg.sender === "bot" ? "justify-start" : "justify-end"
                  }`}
                >
                  {msg.sender === "bot" && (
                    <div className="mb-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--brand-primary-600)] text-white">
                      <Bot size={12} />
                    </div>
                  )}
                  <div
                    className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-snug shadow-sm ${
                      msg.sender === "bot"
                        ? "rounded-bl-sm bg-white text-gray-800"
                        : "rounded-br-sm bg-gradient-to-br from-[var(--brand-primary-700)] to-[var(--brand-secondary-600)] text-white"
                    }`}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-[var(--brand-primary-100)] bg-white px-3 py-3">
              {currentOptions.map((option) =>
                option.next === "root" ? (
                  <button
                    key={option.label}
                    onClick={() => handleOption(option)}
                    className="rounded-full px-3 py-1.5 text-xs font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                  >
                    {option.label}
                  </button>
                ) : (
                  <button
                    key={option.label}
                    onClick={() => handleOption(option)}
                    className="rounded-full bg-gradient-to-br from-[var(--brand-primary-100)] to-[var(--brand-secondary-50)] px-3.5 py-1.5 text-xs font-semibold text-[var(--brand-primary-800)] shadow-sm ring-1 ring-[var(--brand-primary-200)] transition hover:from-[var(--brand-primary-200)] hover:to-[var(--brand-secondary-100)] hover:shadow"
                  >
                    {option.label}
                  </button>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        animate={{ opacity: fadeBubble ? 0.35 : 1, scale: fadeBubble ? 0.85 : 1 }}
        transition={{ duration: 0.2 }}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close chat assistant" : "Open chat assistant"}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[var(--brand-primary-700)] to-[var(--brand-secondary-500)] text-white shadow-lg shadow-[var(--brand-primary-900)]/30"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={isOpen ? "close" : "open"}
            initial={{ opacity: 0, rotate: -45 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 45 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
          </motion.span>
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default ChatBot;
